import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Package, Plus, Search, Edit, Trash2, Eye, X,
  CheckCircle2, Clock, AlertCircle
} from 'lucide-react';

interface ShipmentStatus {
  id: number;
  name: string;
  nameAr?: string;
  description?: string;
  color?: string;
  icon?: string;
  isActive: boolean;
  isFinal: boolean;
  sortOrder: number;
}

export default function ShipmentStatusesPage() {
  const [statuses, setStatuses] = useState<ShipmentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<ShipmentStatus | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<ShipmentStatus | null>(null);
  const [form, setForm] = useState({
    name: '', nameAr: '', description: '', color: '#FFA500',
    icon: '', isActive: true, isFinal: false, sortOrder: '1'
  });

  useEffect(() => { loadStatuses(); }, []);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/shipmentstatuses');
      let data = [];
      if (response.data?.success) {
        data = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }
      setStatuses(data.sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0)));
    } catch (error) {
      console.error('Error loading statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingStatus(null);
    setForm({ name: '', nameAr: '', description: '', color: '#FFA500', icon: '', isActive: true, isFinal: false, sortOrder: '1' });
    setShowModal(true);
  };

  const openEditModal = (status: ShipmentStatus) => {
    setEditingStatus(status);
    setForm({
      name: status.name || '',
      nameAr: status.nameAr || '',
      description: status.description || '',
      color: status.color || '#FFA500',
      icon: status.icon || '',
      isActive: status.isActive !== false,
      isFinal: status.isFinal === true,
      sortOrder: status.sortOrder ? status.sortOrder.toString() : '1'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let color = form.color;
      if (color && !color.startsWith('#')) {
        color = '#' + color;
      }
      
      const payload = {
        name: form.name,
        nameAr: form.nameAr,
        description: form.description,
        color: color,
        icon: form.icon || null,
        isActive: form.isActive,
        isFinal: form.isFinal,
        sortOrder: parseInt(form.sortOrder) || 0
      };
      
      if (editingStatus) {
        await api.put('/api/shipmentstatuses/' + editingStatus.id, payload);
        alert("تم تحديث حالة الشحن بنجاح");
      } else {
        await api.post('/api/shipmentstatuses', payload);
        alert("تم إضافة حالة الشحن بنجاح");
      }
      setShowModal(false);
      loadStatuses();
    } catch (error: any) {
      alert(error.response?.data?.message || "حدث خطأ في الحفظ");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("هل أنت متأكد من حذف حالة الشحن؟")) return;
    try {
      await api.delete('/api/shipmentstatuses/' + id);
      loadStatuses();
      alert("تم حذف حالة الشحن بنجاح");
    } catch (error: any) {
      alert(error.response?.data?.message || "حدث خطأ في الحذف");
    }
  };

  const filteredStatuses = Array.isArray(statuses) ? statuses.filter(s =>
    (s.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.nameAr || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.description || '').toLowerCase().includes(search.toLowerCase())
  ) : [];

  const stats = {
    total: statuses.length,
    active: statuses.filter(s => s.isActive !== false).length,
    final: statuses.filter(s => s.isFinal === true).length,
    inactive: statuses.filter(s => s.isActive === false).length
  };

  const inputClass = "w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition";

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <span>حالات الشحن</span>
              <p className="text-sm text-gray-500 font-normal mt-1">إدارة حالات تتبع الشحنات</p>
            </div>
          </h1>
        </div>
        <Button onClick={openCreateModal} className="bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>إضافة حالة جديدة</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg hover:shadow-xl transition">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">إجمالي الحالات</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg hover:shadow-xl transition">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">الحالات النشطة</p>
            <p className="text-2xl font-bold text-green-700">{stats.active}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg hover:shadow-xl transition">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">الحالات النهائية</p>
            <p className="text-2xl font-bold text-blue-700">{stats.final}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg hover:shadow-xl transition">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-gray-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">الحالات غير النشطة</p>
            <p className="text-2xl font-bold text-gray-700">{stats.inactive}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder="بحث باسم الحالة أو الوصف" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition" />
            </div>
          </div>
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">جاري تحميل الحالات...</p>
            </div>
          ) : filteredStatuses.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">لا توجد حالات</h3>
              <p className="text-gray-500">لا توجد حالات شحن حالياً</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الترتيب</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اسم الحالة (إنجليزي)</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اسم الحالة (عربي)</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اللون</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">نهائية</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الحالة</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStatuses.map((status) => (
                    <tr key={status.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <span className="font-mono font-bold text-orange-600">{status.sortOrder}</span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: status.color || '#FFA500' }}>
                            <Package className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{status.nameAr?.trim() || status.name}</p>
                            {status.description && <p className="text-xs text-gray-500">{status.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className="font-semibold text-gray-700">{status.nameAr || '-'}</span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg border-2 border-gray-200" style={{ backgroundColor: status.color || '#FFA500' }}></div>
                          <span className="font-mono text-xs text-gray-600">{status.color || '-'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        {status.isFinal ? (
                          <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">نعم</span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-50 text-gray-600 rounded-full text-xs font-semibold">لا</span>
                        )}
                      </td>
                      <td className="py-4 px-5">
                        <span className={"px-3 py-1 rounded-full text-xs font-semibold " + (status.isActive !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500')}>
                          {status.isActive !== false ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => { setSelectedStatus(status); setShowViewModal(true); }} className="p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition" title="عرض">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEditModal(status)} className="p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition" title="تعديل">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(status.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title="حذف">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">{editingStatus ? 'تعديل حالة الشحن' : 'إضافة حالة شحن جديدة'}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>اسم الحالة (إنجليزي)</Label>
                    <Input value={form.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })} required className={inputClass} placeholder="Pending" />
                  </div>
                  <div>
                    <Label>اسم الحالة (عربي)</Label>
                    <Input value={form.nameAr} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, nameAr: e.target.value })} className={inputClass} placeholder="قيد الانتظار" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>الوصف</Label>
                    <Input value={form.description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, description: e.target.value })} className={inputClass} placeholder="وصف الحالة" />
                  </div>
                  <div>
                    <Label>اللون</Label>
                    <div className="flex gap-2">
                      <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-16 h-11 rounded-lg border border-gray-200 cursor-pointer" />
                      <Input value={form.color} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, color: e.target.value })} className={inputClass} placeholder="#FFA500" />
                    </div>
                  </div>
                  <div>
                    <Label>ترتيب العرض</Label>
                    <Input type="number" value={form.sortOrder} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, sortOrder: e.target.value })} className={inputClass} placeholder="1" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="isActive" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4" />
                    <label htmlFor="isActive" className="text-sm font-medium">حالة نشطة</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="isFinal" checked={form.isFinal} onChange={(e) => setForm({ ...form, isFinal: e.target.checked })} className="w-4 h-4" />
                    <label htmlFor="isFinal" className="text-sm font-medium">حالة نهائية</label>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">إلغاء</Button>
                  <Button type="submit" className="flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">
                    {editingStatus ? 'تحديث الحالة' : 'إضافة الحالة'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedStatus && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">تفاصيل حالة الشحن</h2>
                <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: selectedStatus.color || '#FFA500' }}>
                    <Package className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{selectedStatus.nameAr?.trim() || selectedStatus.name}</h3>
                    <p className="text-sm text-gray-500">{selectedStatus.nameAr || '-'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">ترتيب العرض</p>
                    <p className="font-semibold">{selectedStatus.sortOrder}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">اللون</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg border-2 border-gray-200" style={{ backgroundColor: selectedStatus.color || '#FFA500' }}></div>
                      <span className="font-mono">{selectedStatus.color || '-'}</span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">الوصف</p>
                    <p className="font-semibold">{selectedStatus.description || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">حالة نهائية</p>
                    <span className={"px-3 py-1 rounded-full text-xs font-semibold " + (selectedStatus.isFinal ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600')}>
                      {selectedStatus.isFinal ? 'نعم' : 'لا'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">الحالة</p>
                    <span className={"px-3 py-1 rounded-full text-xs font-semibold " + (selectedStatus.isActive !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500')}>
                      {selectedStatus.isActive !== false ? 'نشط' : 'غير نشط'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedStatus); }} className="flex-1 bg-orange-500 hover:bg-orange-600">
                    <Edit className="w-4 h-4 ml-2" />
                    تعديل الحالة
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
